/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  DeviceTransactionRequest,
  DeviceTransactionResponse,
  DeviceTransactionInitialProperties,
} from '../../interface/device-transactions.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Thunk to fetch device transactions using the transaction list API.
 * @param {DeviceTransactionRequest} data - The parameters for fetching transactions.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<DeviceTransactionResponse>} Resolves with the device transaction response data.
 */
export const fetchDeviceTransaction = createAsyncThunk<
  DeviceTransactionResponse,
  DeviceTransactionRequest
>(
  'device-transaction/fetchDeviceTransaction',
  async (data: DeviceTransactionRequest, { dispatch }) => {
    try {
      const sn = data.chargingPointSn || data.chargingpointsn;
      const accountCustomIdValue = (data as any).accountCustomId;
      const station = (data as any).stationId || (data as any).chargingStation;

      const queryData: Record<string, any> = { ...data };
      delete queryData.chargingpointsn;
      delete queryData.chargingPointSn;
      delete queryData.accountCustomId;
      delete queryData.chargingStation;
      delete queryData.stationId;

      if (sn) {
        queryData.chargingPointSn = Array.isArray(sn) ? sn : [sn];
      }
      if (accountCustomIdValue) {
        queryData.accountCustomId = Array.isArray(accountCustomIdValue)
          ? accountCustomIdValue
          : [accountCustomIdValue];
      }
      if (station) {
        queryData.stationId = Array.isArray(station) ? station : [station];
      }

      const queryParameters = removeEmptyKeys(queryData);
      const response = await cpoTransactionApi.fetchCpoTransactions(queryParameters);
      return {
        ...response.data,
        results: (response.data?.results || []).map((roleData: any) => ({
          ...roleData,
          date: ConvertUTCtoDate(roleData.createdAt),
          time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
          id: roleData._id || roleData.id,
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to refresh device transactions using the transaction list API.
 * @param {DeviceTransactionRequest} data - The parameters for refreshing transactions.
 * @returns {Promise<DeviceTransactionResponse>} Resolves with the refreshed device transaction response data.
 */
export const refreshDeviceTransaction = createAsyncThunk<
  DeviceTransactionResponse,
  DeviceTransactionRequest
>(
  'device-transaction/refreshDeviceTransaction',
  async (data: DeviceTransactionRequest) => {
    const sn = data.chargingPointSn || data.chargingpointsn;
    const accountCustomIdValue = (data as any).accountCustomId;
    const station = (data as any).stationId || (data as any).chargingStation;

    const queryData: Record<string, any> = { ...data };
    delete queryData.chargingpointsn;
    delete queryData.chargingPointSn;
    delete queryData.accountCustomId;
    delete queryData.chargingStation;
    delete queryData.stationId;

    if (sn) {
      queryData.chargingPointSn = Array.isArray(sn) ? sn : [sn];
    }
    if (accountCustomIdValue) {
      queryData.accountCustomId = Array.isArray(accountCustomIdValue)
        ? accountCustomIdValue
        : [accountCustomIdValue];
    }
    if (station) {
      queryData.stationId = Array.isArray(station) ? station : [station];
    }

    const queryParameters = removeEmptyKeys(queryData);
    const response = await cpoTransactionApi.fetchCpoTransactions(queryParameters);
    return {
      ...response.data,
      results: (response.data?.results || []).map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
        id: roleData._id || roleData.id,
      })),
    };
  },
);

/**
 * Thunk to fetch transaction list filters.
 * @param {object} [_payload] - Optional filter query payload.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<CommonFilterProperties[]>} Resolves with array of filter properties.
 */
export const fetchListTransactionFilters = createAsyncThunk<
  CommonFilterProperties[],
  { chargingStation?: string; chargingPointSn?: string } | undefined
>('device-transaction/fetchListTransactionFilters', async (_payload, { dispatch }) => {
  try {
    const response = await cpoTransactionApi.fetchCpoTransactionFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
      response.data,
    ).filter((filter) => filter.type !== filterType.DATE_RANGE);
    return updatedResponse;
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message || 'Something went wrong!',
        code: error?.response?.data?.code || 200,
        status: true,
      }),
    );
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DeviceTransactionInitialProperties = {
  transactions: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for managing device transaction state.
 */
export const deviceTransactionSlice = createSlice({
  name: 'device-transaction',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateTransactionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.sortBy = null;
        state.sortField = null;
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableTransactionFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceTransaction.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDeviceTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchDeviceTransaction.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(refreshDeviceTransaction.fulfilled, (state, action) => {
      state.transactions = [...action.payload.results];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchListTransactionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListTransactionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...deviceFilter, ...action.payload].filter(
            (object, index, self) =>
              index === self.findIndex((t) => t.key === object.key) &&
              object.type !== filterType.DATE_RANGE,
          );
        },
      )
      .addCase(fetchListTransactionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

/**
 * Legacy alias export for guestChargingSlice.
 */
export const guestChargingSlice = deviceTransactionSlice;

// Export the actions
export const {
  updateTransactionFilters,
  disableTransactionFiltersApplied,
  setSortData,
} = deviceTransactionSlice.actions;
// Export the reducer
export default deviceTransactionSlice.reducer;

